import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { auth, coins, tags, sets } from '../utils/api';
import TagSelector from './shared/TagSelector';
import CreateTagModal from './CreateTagModal';

const BulkTagEditor = ({ isOpen, onClose, selectedCoins, onUpdate }) => {
  const tagSelectorRef = useRef(null);
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userSets, setUserSets] = useState([]);
  const [loadingSets, setLoadingSets] = useState(true);
  const [selectedSet, setSelectedSet] = useState('');
  const [mode, setMode] = useState('sets'); // 'tags' or 'sets'
  const [tagCounts, setTagCounts] = useState({});
  const [showCreateTagModal, setShowCreateTagModal] = useState(false);

  // Fetch tag counts for selected coins
  useEffect(() => {
    const fetchTagCounts = async () => {
      if (!isOpen || selectedCoins.size === 0) return;
      
      try {
        const counts = {};
        await Promise.all(Array.from(selectedCoins).map(async (coinId) => {
          const coin = await coins.getById(coinId);
          if (coin.tags) {
            coin.tags.forEach(tag => {
              counts[tag.id] = (counts[tag.id] || 0) + 1;
            });
          }
        }));
        setTagCounts(counts);
      } catch (err) {
        console.error('Failed to fetch tag counts:', err);
        toast.error('Failed to load tag information');
      }
    };

    fetchTagCounts();
  }, [isOpen, selectedCoins]);

  // Fetch available sets
  useEffect(() => {
    const initializeModal = async () => {
      try {
        const userSets = await sets.getAll();
        setUserSets(userSets);
      } catch (err) {
        console.error('Failed to fetch sets:', err);
        toast.error('Failed to load sets');
      } finally {
        setLoadingSets(false);
      }
    };
    if (isOpen) {
      initializeModal();
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (mode === 'tags' && selectedTagIds.length === 0) {
      toast.error('Please select at least one tag');
      return;
    }

    if (mode === 'sets' && !selectedSet) {
      toast.error('Please select a set');
      return;
    }

    setLoading(true);
    try {
      if (mode === 'tags') {
        // Use bulk operation to add tags to all coins at once
        await tags.addTagsToCoins(
          Array.from(selectedCoins),
          selectedTagIds
        );
        toast.success('Tags updated successfully');
      } else {
        // Add coins to selected set
        await sets.addCoins(selectedSet, Array.from(selectedCoins));
        toast.success('Coins added to set successfully');
      }
      onUpdate();
      onClose();
    } catch (err) {
      toast.error(err.message || 'Failed to update tags/sets');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveTags = async () => {
    if (selectedTagIds.length === 0) {
      toast.error('Please select at least one tag');
      return;
    }

    setLoading(true);
    try {
      // Use bulk operation to remove tags from all coins at once
      await tags.removeTagsFromCoins(
        Array.from(selectedCoins),
        selectedTagIds
      );
      toast.success('Tags removed successfully');
      onUpdate();
      onClose();
    } catch (err) {
      toast.error(err.message || 'Failed to remove tags');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFromSet = async () => {
    if (!selectedSet) {
      toast.error('Please select a set');
      return;
    }

    setLoading(true);
    try {
      // Remove coins from selected set
      await sets.removeCoins(selectedSet, Array.from(selectedCoins));
      toast.success('Coins removed from set successfully');
      onUpdate();
      onClose();
    } catch (err) {
      toast.error(err.message || 'Failed to remove coins from set');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 max-w-lg w-full">
        <div className="flex justify-between items-start mb-4">
          <h2 className="text-xl font-bold text-gray-900">
            Edit {selectedCoins.size} {selectedCoins.size === 1 ? 'Coin' : 'Coins'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <span className="sr-only">Close</span>
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Mode Toggle */}
        <div className="mb-6">
          <div className="flex rounded-md shadow-sm" role="group">
            <button
              type="button"
              onClick={() => setMode('sets')}
              className={`px-4 py-2 text-sm font-medium rounded-l-md ${
                mode === 'sets'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              } border border-gray-300`}
            >
              Manage Sets
            </button>
            <button
              type="button"
              onClick={() => setMode('tags')}
              className={`px-4 py-2 text-sm font-medium rounded-r-md ${
                mode === 'tags'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              } border border-gray-300 border-l-0`}
            >
              Manage Tags
            </button>
          </div>
        </div>

        {mode === 'tags' ? (
          // Tag Selection UI
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Select Tags
              </label>
              <button 
                onClick={() => setShowCreateTagModal(true)}
                className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Create New Tag
              </button>
            </div>
            <div className="relative border border-gray-300 rounded-md">
              <TagSelector
                ref={tagSelectorRef}
                selectedTagIds={selectedTagIds}
                onChange={setSelectedTagIds}
                className="w-full"
              />
            </div>
          </div>
        ) : (
          // Set Selection UI
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2 sticky top-0 bg-white z-10">
              Select Set
            </label>
            <div className="h-full">
              {loadingSets ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                </div>
              ) : userSets.length === 0 ? (
                <p className="text-sm text-gray-500">
                  No sets available. Create one{' '}
                  <Link 
                    to="/sets"
                    onClick={onClose}
                    className="text-indigo-600 hover:text-indigo-500"
                  >
                    here
                  </Link>
                </p>
              ) : (
                <select
                  value={selectedSet}
                  onChange={(e) => setSelectedSet(e.target.value)}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                >
                  <option value="">Select a set...</option>
                  {userSets.map((set) => (
                    <option key={set.id} value={set.id}>
                      {set.name} ({set.coin_count} {set.coin_count === 1 ? 'coin' : 'coins'})
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-4">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Cancel
          </button>
          {mode === 'tags' ? (
            <button
              onClick={handleRemoveTags}
              disabled={loading || selectedTagIds.length === 0}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Removing...' : 'Remove Tags'}
            </button>
          ) : mode === 'sets' && (
            <button
              onClick={handleRemoveFromSet}
              disabled={loading || !selectedSet}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Removing...' : 'Remove from Set'}
            </button>
          )}
          <button
            onClick={handleSubmit}
            disabled={loading || (mode === 'sets' && !selectedSet) || (mode === 'tags' && selectedTagIds.length === 0)}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Updating...' : mode === 'tags' ? 'Add Tags to Coins' : 'Add to Set'}
          </button>
        </div>
      </div>
      {/* Create Tag Modal */}
      <CreateTagModal
        isOpen={showCreateTagModal}
        onClose={() => setShowCreateTagModal(false)}
        onTagCreated={(newTag) => {
          setShowCreateTagModal(false);
          // Add the new tag to selected tags
          setSelectedTagIds([...selectedTagIds, newTag.id]);
          // Refresh the tag selector to show the new tag
          if (tagSelectorRef.current) {
            tagSelectorRef.current.refresh();
          }
        }}
      />
    </div>
  );
};

export default BulkTagEditor;
